import React, { useEffect, useMemo, useState, useRef, useCallback, memo } from 'react'
import { Modal, View, Text, Button, Avatar, Icon } from '@/components'
import { createStyles } from '@codeleap/styles'
import { useStateStore, useTranslationContext } from 'stream-chat-react'
import type { Poll as PollClass, PollVote, PollState } from 'stream-chat'
import { isVoteAnswer } from 'stream-chat'
import { textVariant } from '@/config'

type PollDetailsModalProps = {
  poll: PollClass
  visible: boolean
  onClose: () => void
}

type PollDetailsData = {
  name: string
  options: Array<{ id: string; text: string }>
  vote_counts_by_option: Record<string, number>
  latest_votes_by_option: Record<string, PollVote[]>
  maxVotedOptionIds: string[]
}

const pollDetailsSelector = (state: PollState): PollDetailsData => ({
  name: state.name,
  options: state.options,
  vote_counts_by_option: state.vote_counts_by_option,
  latest_votes_by_option: state.latest_votes_by_option,
  maxVotedOptionIds: state.maxVotedOptionIds,
})

const MAX_VISIBLE_VOTES = 2

// Memoized voter row component to prevent unnecessary re-renders
const VoterRow = memo(({ vote, optionId, index }: { vote: PollVote; optionId: string; index: number }) => {
  const userImage = vote.user?.image
  const imageUrl = typeof userImage === 'string' ? userImage : undefined

  return (
    <View key={`${optionId}-vote-${vote.user?.id || `voter-${index}`}`} style={styles.voterRow}>
      <Avatar
        image={imageUrl as any}
        name={vote.user?.name}
        style={styles.voterAvatarComposed}
        debugName='PollDetails:Avatar'
      />
      <Text text={vote.user?.name || 'Unknown'} style={styles.voterName} />
      <Text
        text={vote.created_at ? formatVoteTime(vote.created_at) : ''}
        style={styles.voteTime}
      />
    </View>
  )
})

// Memoized option card component
const OptionCard = memo(({
  option,
  votes,
  isTopOption,
  allVotes,
  isExpanded,
  hasMoreVotes,
  hasMoreVotesThanShown,
  onToggleExpanded,
  t,
}: {
  option: { id: string; text: string }
  votes: number
  isTopOption: boolean
  allVotes: PollVote[]
  isExpanded: boolean
  hasMoreVotes: boolean
  hasMoreVotesThanShown: boolean
  onToggleExpanded: (optionId: string) => void
  t: any
}) => {
  const displayedVotes = isExpanded ? allVotes : allVotes.slice(0, MAX_VISIBLE_VOTES)

  const handleToggle = useCallback(() => {
    onToggleExpanded(option.id)
  }, [onToggleExpanded, option.id])

  return (
    <View style={styles.optionCard}>
      <View style={styles.optionHeader}>
        <Text text={option.text} style={styles.optionText} />
        <View style={styles.voteCountWrapper}>
          <Text
            text={`${votes} ${votes === 1 ? 'vote' : 'votes'}`}
            style={styles.voteCount}
          />
          {isTopOption && (
            <Icon name='star' style={styles.starIcon} />
          )}
        </View>
      </View>

      {displayedVotes.length > 0 && (
        <View style={styles.votersList}>
          {displayedVotes.map((vote: PollVote, index: number) => (
            <VoterRow
              key={`${option.id}-vote-${vote.user?.id || `voter-${index}`}`}
              vote={vote}
              optionId={option.id}
              index={index}
            />
          ))}
        </View>
      )}

      {(hasMoreVotes || hasMoreVotesThanShown) && (
        <Button
          text={isExpanded ? t?.('Show less') || 'Show less' : t?.('Show all') || 'Show all'}
          style={[styles.showAllButtonComposed, 'minimal']}
          debugName={`PollDetails:ShowAll:${option.id}`}
          onPress={handleToggle}
        />
      )}
    </View>
  )
})

export const PollDetailsModal = memo(({ poll, visible, onClose }: PollDetailsModalProps) => {
  const { t } = useTranslationContext()
  const { name, options, vote_counts_by_option, latest_votes_by_option } = useStateStore(
    poll.state,
    pollDetailsSelector,
  )

  const [expandedOptions, setExpandedOptions] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [fullVotesByOption, setFullVotesByOption] = useState<Record<string, PollVote[]>>({})
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    if (visible) {
      setLoading(true)
      poll.query(poll.id)
        .then(() => {
          if (!cancelled && mountedRef.current) {
            setLoading(false)
          }
        })
        .catch(error => {
          console.error('Failed to refresh poll', error)
          if (!cancelled && mountedRef.current) {
            setLoading(false)
          }
        })
    }

    return () => {
      cancelled = true
    }
  }, [visible, poll])

  const sortedOptions = useMemo(
    () => [...(options || [])].sort(
      (a, b) => (vote_counts_by_option?.[b.id] ?? 0) - (vote_counts_by_option?.[a.id] ?? 0),
    ),
    [options, vote_counts_by_option],
  )

  const maxVotes = useMemo(
    () => Math.max(...Object.values(vote_counts_by_option || {}).map(v => typeof v === 'number' ? v : 0), 0),
    [vote_counts_by_option],
  )

  const fetchAllVotesForOption = useCallback(async (optionId: string) => {
    if (fullVotesByOption[optionId]) {
      return
    }

    try {
      const votesResponse = await poll.queryOptionVotes({
        filter: { option_id: optionId },
        options: { limit: 100 },
      })
      const allVotes = votesResponse?.votes?.filter(vote => !isVoteAnswer(vote)) ?? []

      if (mountedRef.current) {
        setFullVotesByOption(prev => ({
          ...prev,
          [optionId]: allVotes,
        }))
      }
    } catch (error) {
    }
  }, [poll, fullVotesByOption])

  const toggleExpanded = useCallback((optionId: string) => {
    const isCurrentlyExpanded = expandedOptions[optionId]

    setExpandedOptions(prev => ({
      ...prev,
      [optionId]: !prev[optionId],
    }))

    if (!isCurrentlyExpanded) {
      const count = vote_counts_by_option?.[optionId] ?? 0
      const latestVotes = latest_votes_by_option?.[optionId]?.filter(vote => !isVoteAnswer(vote)) ?? []

      if (count > latestVotes.length) {
        fetchAllVotesForOption(optionId)
      }
    }
  }, [expandedOptions, vote_counts_by_option, latest_votes_by_option, fetchAllVotesForOption])

  // Pre-compute option data to avoid calculations during render
  const optionsData = useMemo(() => {
    return sortedOptions.map(option => {
      const count = vote_counts_by_option?.[option.id]
      const votes = typeof count === 'number' ? count : 0
      const isTopOption = votes === maxVotes && votes > 0

      const latestVotes = latest_votes_by_option?.[option.id]?.filter(vote => !isVoteAnswer(vote)) ?? []
      const fullVotes = fullVotesByOption[option.id]
      const allVotes = fullVotes && fullVotes.length > 0 ? fullVotes : latestVotes

      const hasMoreVotes = allVotes.length > MAX_VISIBLE_VOTES
      const hasMoreVotesThanShown = votes > allVotes.length

      return {
        option,
        votes,
        isTopOption,
        allVotes,
        hasMoreVotes,
        hasMoreVotesThanShown,
      }
    })
  }, [sortedOptions, vote_counts_by_option, latest_votes_by_option, maxVotes, fullVotesByOption])

  return (
    <Modal
      title={t ? t('Poll details') : 'Poll details'}
      visible={visible}
      toggle={onClose}
      showClose
      style={styles.modalComposed}
    >
      <View style={styles.wrapper}>
        <Text text={t ? t('Question') : 'Question'} style={styles.sectionLabel} />

        <View style={styles.questionCard}>
          <Text text={name} style={styles.questionText} />
        </View>

        <View style={styles.optionList}>
          {optionsData.map(({ option, votes, isTopOption, allVotes, hasMoreVotes, hasMoreVotesThanShown }) => (
            <OptionCard
              key={option.id}
              option={option}
              votes={votes}
              isTopOption={isTopOption}
              allVotes={allVotes}
              isExpanded={!!expandedOptions[option.id]}
              hasMoreVotes={hasMoreVotes}
              hasMoreVotesThanShown={hasMoreVotesThanShown}
              onToggleExpanded={toggleExpanded}
              t={t}
            />
          ))}
        </View>
      </View>
    </Modal>
  )
})

const formatVoteTime = (date: Date | string) => {
  const voteDate = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diff = now.getTime() - voteDate.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US'

  if (days === 0) {
    return `today ${voteDate.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })}`
  } else if (days === 1) {
    return `yesterday ${voteDate.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })}`
  } else {
    return voteDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  }
}

const styles = createStyles(theme => ({
  // Pre-composed styles to avoid inline object creation on every render
  modalComposed: [
    'center',
    'box:noPadding',
    {
      box: {
        maxWidth: 420,
        width: '100%',
        [theme.media.down('tabletSmall')]: {
          maxWidth: `calc(100% - ${theme.spacing.value(4)}px)`,
        },
      },
      titleWrapper: {
        ...theme.spacing.padding(2),
        ...theme.spacing.paddingBottom(1),
      },
    },
  ] as any,
  voterAvatarComposed: [
    'small',
    {
      wrapper: {
        width: 32,
        height: 32,
        minWidth: 32,
        minHeight: 32,
      },
    },
  ] as any,
  showAllButtonComposed: [
    'ghost',
    {
      alignSelf: 'center',
      touchableWrapper: {
        minWidth: theme.spacing.value(10),
      },
    },
  ] as any,
  wrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(2),
    ...theme.spacing.padding(2),
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  sectionLabel: {
    ...textVariant('c1'),
    fontWeight: 600,
    color: theme.colors.textHeadingRegular,
  },
  questionCard: {
    ...theme.presets.column,
    ...theme.spacing.padding(1.5),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.colors.bgDefault,
    boxShadow: '0px 12px 24px rgba(15, 23, 42, 0.08)',
  },
  questionText: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  optionList: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.5),
  },
  optionCard: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.5),
    ...theme.spacing.padding(1.5),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.colors.bgDefault,
    boxShadow: '0px 12px 24px rgba(15, 23, 42, 0.08)',
  },
  optionHeader: {
    ...theme.presets.row,
    ...theme.presets.justifySpaceBetween,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(1),
  },
  optionText: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    flex: 1,
  },
  voteCountWrapper: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(0.5),
    flexShrink: 0,
  },
  voteCount: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  starIcon: {
    ...theme.sized(2),
    color: theme.baseColors.primarySolid500,
  },
  votersList: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  voterRow: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(1),
  },
  voterName: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
    flex: 1,
  },
  voteTime: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
    flexShrink: 0,
  },
}))

